import {
  createContext,
  useContext,
  useRef,
  useCallback,
  ReactNode,
  useState,
  useEffect
} from 'react'
import { useSelector } from 'react-redux'
import { videoTypes } from 'consts'

import {
  ICanvasObject,
  IProjectSound,
  ProcessingEvent,
  WebWoodEngine
} from '@renderforest/webwood-engine'

import { projectDataSelectors } from 'store/features/projectData/selectors'
import { IThumbReadyData, videoThumbsService } from 'services/video-thumbs'
import { CANVAS_ID, MOBILE_SIZE_BREAKPOINT } from 'consts'
import projectDataSlice from 'store/features/projectData/slice'

import { IMediaItem, TMediaItemType } from 'types/media'
import { IAudioItem } from 'types/audio'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import {
  convertAudioItemsToProjectSounds,
  convertMediaItemsToCanvasObjects
} from '@renderforest/v2ww-data-converter'
import { getEnvs } from 'utils/envs'
import { IItemClickHandlerParams } from 'store/types/editor'
import { useResponsive } from 'hooks/useResponsive'
import { FileRegistry } from 'utils/file-registry/FileRegistry'
import debounce from 'lodash.debounce'
import { LOADER_ASSETS, WebwoodMediaItemLoader } from './Loader'

const onCanvasClick = (targets: ICanvasObject[]) => {
  // const activeTimelineItems: TActiveTimelineItems = targets.map(target => ({
  //   id: target.originalId,
  //   type: target.originalType as TMediaItemType
  // }))
  // dispatch(editorThunks.setActiveTimelineItems(activeTimelineItems))
  const firstTarget = targets[0]
  if (!firstTarget) return
  const activeTimelineItem: IItemClickHandlerParams = {
    id: targets[0].originalId,
    type: targets[0].originalType as TMediaItemType
  }
  dispatch(editorThunks.selectSingleTimelineItem(activeTimelineItem))
}

interface IWebWoodContextType {
  canvasElementRef: React.RefObject<HTMLCanvasElement | null>
  onCanvasElementReady: (canvasElement: HTMLCanvasElement) => void
  engine: WebWoodEngine | null
  updateCanvasObjects: (mediaItems: IMediaItem[]) => void
  updateCanvasAudios: (multilineAudios: IAudioItem[]) => void
  updateCanvasDimensions: (width: number, height: number) => void
  updateCanvasPlaybackTime: (time: number) => void
  updateObjectById: (id: string, object: Partial<ICanvasObject>) => void
  getObjectById: (id: string) => ICanvasObject | undefined
  /**
   * Returns the latest known max character count that can still fit inside
   * the GPU texture limit for the given text object id, or undefined if the
   * engine has not yet reported a value for that id.
   */
  getMaxAvailableChars: (id: string) => number | undefined
  play: (time: number) => void
  pause: () => void
}

const WebWoodContext = createContext<IWebWoodContextType | null>(null)

const onObjectDimensionsResolved = ({
  id,
  width,
  height,
  x,
  y
}: {
  id: string
  width: number
  height: number
  x?: number
  y?: number
}) => {
  const updates: Record<string, unknown> = { width, height }

  if (x !== undefined && y !== undefined) {
    updates.coordinates = { x, y }
  }

  // Engine-driven dimension updates are derived state from a user action that
  // already created an undo entry (e.g. font / fontSize / stroke change).
  // Dispatching another undoable update here pushes a second history entry,
  // so the first Undo only reverts dimensions, leaving the font on the new value.
  // Use the non-undoable action to avoid creating a duplicate history entry.
  dispatch(
    projectDataSlice.actions.updateMediaItemWithoutUndoSave({
      id,
      updates,
      isCheckAssetId: false
    })
  )
}

const onMediaBufferingStateChanged = debounce((isBuffering: boolean) => {
  dispatch(editorThunks.setIsMediaBuffering(isBuffering))
}, 200)

const { MEDIA_BUNNY_VERSION } = getEnvs()

export const WebWoodContextProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const isMobile = useResponsive(MOBILE_SIZE_BREAKPOINT)
  const canvasElementRef = useRef<HTMLCanvasElement>(null)
  const [engine, setEngine] = useState<WebWoodEngine | null>(null)

  const mediaItemsRef = useRef<IMediaItem[]>([])
  const canvasObjectTypesRef = useRef<Map<string, string>>(new Map())
  const textMaxAvailableCharsRef = useRef<Map<string, number>>(new Map())
  const [isThumbsInitialized, setIsThumbsInitialized] = useState(false)
  const { width: projectWidth, height: projectHeight } = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const mediaItems = useSelector(projectDataSelectors.mediaItems)

  const onThumbReady = useCallback((data: IThumbReadyData) => {
    videoThumbsService.onThumbReady(data)
  }, [])

  const createWebwoodEngine = useCallback(() => {
    const mediaBunnyUrl = window.MEDIA_BUNNY_JS
      ? window.MEDIA_BUNNY_JS.replace('.mjs', '.cjs')
      : `https://cdn.renderforest.com/mediabunny/${MEDIA_BUNNY_VERSION}/mediabunny.min.cjs`
    WebWoodEngine.createInstance(
      { width: projectWidth, height: projectHeight },
      engine => {
        setEngine(engine)
        engine.signals.on(
          ProcessingEvent.BUFFERING_STATE_CHANGED,
          onMediaBufferingStateChanged
        )
        engine.signals.on(ProcessingEvent.THUMB_READY, onThumbReady)
        engine.signals.on(
          ProcessingEvent.OBJECT_DIMENSIONS_RESOLVED,
          onObjectDimensionsResolved
        )

        engine.setOnClickListener(onCanvasClick)
      },
      CANVAS_ID,
      {
        mediaBunnyUrl,
        fileRegistry: FileRegistry.getInstance(),
        isMobile,
        loader: {
          class: WebwoodMediaItemLoader,
          assets: LOADER_ASSETS
        }
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCanvasElementReady = useCallback(
    (canvasElement: HTMLCanvasElement) => {
      if (!canvasElement) return
      canvasElementRef.current = canvasElement
      createWebwoodEngine()
    },
    [createWebwoodEngine]
  )

  const updateCanvasObjects = useCallback(
    (mediaItems: IMediaItem[]) => {
      if (!engine) return

      mediaItemsRef.current = mediaItems
      const canvasObjects = convertMediaItemsToCanvasObjects(mediaItems, true)

      // Detect ids whose objectType transitioned (e.g. loader -> image/video
      // when generation completes). The engine's updateObjects path patches
      // configs in place and does not recreate the Phaser instance, so the
      // loader would otherwise stick around. Force a remove+recreate by first
      // updating with those ids excluded, then with the full list.
      const transitionedIds = new Set<string>()
      canvasObjects.forEach(object => {
        const previousType = canvasObjectTypesRef.current.get(object.id)
        if (previousType && previousType !== object.objectType) {
          transitionedIds.add(object.id)
        }
      })

      if (transitionedIds.size > 0) {
        engine.updateObjects(
          canvasObjects.filter(object => !transitionedIds.has(object.id))
        )
      }
      engine.updateObjects(canvasObjects)

      const nextTypes = new Map<string, string>()
      canvasObjects.forEach(object => {
        nextTypes.set(object.id, object.objectType)
      })
      canvasObjectTypesRef.current = nextTypes

      if (isThumbsInitialized) {
        const videoAssetIds = [
          ...new Set(
            mediaItems
              .filter(item => videoTypes.includes(item.type))
              .map(item => item.assetId)
          )
        ]
        const cachedMap = videoThumbsService.getCachedTimestamps(videoAssetIds)
        engine.startThumbnailExtraction(cachedMap)
      }
    },
    [engine, isThumbsInitialized]
  )

  const updateCanvasAudios = useCallback(
    (multilineAudios: IAudioItem[]) => {
      if (!engine) return
      const projectSounds: IProjectSound[] =
        convertAudioItemsToProjectSounds(multilineAudios)
      engine.updateProjectSounds(projectSounds)
    },
    [engine]
  )

  const updateCanvasDimensions = useCallback(
    (width: number, height: number, optimize: boolean = true) => {
      if (!engine) return

      if (optimize) {
        const optimalCanvasSize = 720
        engine.setSizeLimitation(optimalCanvasSize)
        const smallestSide = Math.min(width, height)
        const mobileScale = smallestSide / optimalCanvasSize
        const finalWidth = Math.min(width / mobileScale, width)
        const finalHeight = Math.min(height / mobileScale, height)
        engine.setDimensions(finalWidth, finalHeight)
      } else {
        engine.setDimensions(width, height)
      }
    },
    [engine]
  )

  const updateCanvasPlaybackTime = useCallback(
    (time: number) => {
      if (!engine) return
      engine.setCurrentTime(time * 1000)
    },
    [engine]
  )

  const play = useCallback(
    (time: number) => {
      if (!engine) return
      engine.play(time * 1000)
    },
    [engine]
  )

  const pause = useCallback(() => {
    if (!engine) return
    engine.pause()
  }, [engine])

  useEffect(() => {
    videoThumbsService.initialize().then(() => setIsThumbsInitialized(true))
  }, [])

  useEffect(() => {
    if (!engine || !isThumbsInitialized) return
    const videoAssetIds = [
      ...new Set(
        mediaItems
          .filter(item => videoTypes.includes(item.type))
          .map(item => item.assetId)
      )
    ]
    const cachedMap = videoThumbsService.getCachedTimestamps(videoAssetIds)
    engine.startThumbnailExtraction(cachedMap)
  }, [engine, mediaItems, isThumbsInitialized])

  /**
   * Destroy the canvas instance on unmount
   * Remove the on canvas click listener
   * Destroy the engine
   */
  useEffect(
    () => () => {
      if (!engine) return
      engine.signals.off(
        ProcessingEvent.BUFFERING_STATE_CHANGED,
        onMediaBufferingStateChanged
      )
      engine.signals.off(ProcessingEvent.THUMB_READY, onThumbReady)
      engine.signals.off(
        ProcessingEvent.OBJECT_DIMENSIONS_RESOLVED,
        onObjectDimensionsResolved
      )

      engine.removeOnClickListener(onCanvasClick)
      engine.destroy(true)
    },
    [engine, onThumbReady]
  )

  const updateObjectById = useCallback(
    (id: string, object: Partial<ICanvasObject>) => {
      if (!engine) return
      engine.updateObjectById(id, object)
    },
    [engine]
  )

  const getObjectById = useCallback(
    (id: string) => {
      if (!engine) return undefined
      return engine.getObjectById(id)
    },
    [engine]
  )

  const getMaxAvailableChars = useCallback(
    (id: string) => textMaxAvailableCharsRef.current.get(id),
    []
  )

  return (
    <WebWoodContext.Provider
      value={{
        canvasElementRef,
        onCanvasElementReady,
        engine,
        updateCanvasObjects,
        updateCanvasAudios,
        updateCanvasDimensions,
        updateCanvasPlaybackTime,
        updateObjectById,
        getObjectById,
        getMaxAvailableChars,
        play,
        pause
      }}
    >
      {children}
    </WebWoodContext.Provider>
  )
}

export const useWebWoodContext = () => {
  const context = useContext(WebWoodContext)
  if (!context) {
    throw new Error(
      'useWebWoodContext must be used within a WebWoodContextProvider'
    )
  }
  return context
}
